// Helpers for the lottery-style number generators (Lottery / Powerball / Mega
// Millions). These presets store a JSON object describing the drawn tickets;
// we parse it for the <LotteryBalls /> visual renderer and format it back to
// readable text for copy/export so raw JSON never leaks to the user.
//
// Two shapes exist. Since Oct 2026 the tickets are drawn in code
// (@/lib/lotteryCompletion) and the JSON names the game, its number span and
// the bonus ball; the stats are worked out here. Older rows were written by a
// model and carry an "analysis" sentence per set instead.

export type LotteryStats = {
  odd: number;
  even: number;
  low: number;
  high: number;
  sum: number;
};

export type LotterySet = {
  main: number[];
  // Bonus/special balls. Powerball & Mega Millions emit a single number;
  // the generic lottery generator can emit several (e.g. EuroMillions' two
  // Lucky Stars), so this is always normalized to an array.
  specials?: number[];
  /** Older model-written rows only. */
  analysis?: string;
  stats?: LotteryStats;
};

export type LotteryNotice = 'rules_ignored' | 'exclusions_ignored';

export type LotteryOutput = {
  sets: LotterySet[];
  /** The game's name ("Powerball", "EuroMillions", "Custom"). */
  game?: string;
  /** What one bonus ball is called ("Powerball", "Lucky Star"). */
  bonusLabel?: string;
  /** Digit games (4D): each set is one ticket's digits, in order. */
  digits?: boolean;
  notices: LotteryNotice[];
};

export const LOTTERY_PRESETS = [
  'lottery-number-generator',
  'powerball-number-generator',
  'mega-millions-number-generator',
] as const;

export function isLotteryPreset(slug?: string | null): boolean {
  return !!slug && (LOTTERY_PRESETS as readonly string[]).includes(slug);
}

export function lotterySpecialLabel(slug?: string | null): string | undefined {
  if (slug === 'powerball-number-generator') return 'Powerball';
  if (slug === 'mega-millions-number-generator') return 'Mega Ball';
  return undefined;
}

type RawLotterySet = { main?: unknown; special?: unknown; analysis?: unknown };

function toSpecials(raw: unknown): number[] | undefined {
  const values = Array.isArray(raw) ? raw : [raw];
  const specials = values
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n));
  return specials.length > 0 ? specials : undefined;
}

function normalizeLotterySets(raw: RawLotterySet[]): LotterySet[] {
  return raw
    .map((e) => ({
      main: Array.isArray(e?.main)
        ? e.main.map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [],
      specials: toSpecials(e?.special),
      analysis:
        typeof e?.analysis === 'string' && e.analysis.trim()
          ? e.analysis.trim()
          : undefined,
    }))
    .filter((s) => s.main.length > 0);
}

/** Odd/even, low/high (lower or upper half of the span) and the sum. */
export function lotteryStats(
  main: number[],
  [min, max]: [number, number],
): LotteryStats {
  const middle = (min + max) / 2;
  const odd = main.filter((n) => n % 2 !== 0).length;
  const low = main.filter((n) => n < middle).length;
  return {
    odd,
    even: main.length - odd,
    low,
    high: main.length - low,
    sum: main.reduce((total, n) => total + n, 0),
  };
}

const NOTICES: readonly LotteryNotice[] = [
  'rules_ignored',
  'exclusions_ignored',
];

/** The current shape: game, span and stats flag beside the sets. */
function fromDrawJson(parsed: Record<string, unknown>): LotteryOutput | null {
  if (!Array.isArray(parsed.sets)) return null;
  const sets = normalizeLotterySets(parsed.sets as RawLotterySet[]);
  if (!sets.length) return null;
  const span =
    Array.isArray(parsed.range) &&
    parsed.range.length === 2 &&
    parsed.range.every((n) => Number.isFinite(Number(n)))
      ? ([Number(parsed.range[0]), Number(parsed.range[1])] as [number, number])
      : null;
  const digits = parsed.digits === true;
  return {
    sets:
      parsed.stats === true && span && !digits
        ? sets.map((set) => ({ ...set, stats: lotteryStats(set.main, span) }))
        : sets,
    game: typeof parsed.game === 'string' ? parsed.game : undefined,
    bonusLabel: typeof parsed.bonus === 'string' ? parsed.bonus : undefined,
    digits,
    notices: Array.isArray(parsed.notices)
      ? (parsed.notices.filter((notice) =>
          NOTICES.includes(notice as LotteryNotice),
        ) as LotteryNotice[])
      : [],
  };
}

/**
 * Parse a stored lottery output. Tolerant of code fences, surrounding prose,
 * and malformed JSON (the older model-written rows sometimes had a stray
 * brace): tries a strict JSON parse first, then falls back to extracting each
 * set's fields by hand. Returns null if no sets are found (caller falls back
 * to text).
 */
export function parseLotteryOutput(text?: string | null): LotteryOutput | null {
  if (!text || typeof text !== 'string') return null;

  // 1) Strict JSON parse of the first {...} block.
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start !== -1 && end > start) {
    try {
      const parsed = JSON.parse(text.slice(start, end + 1));
      if (parsed && typeof parsed === 'object') {
        const output = fromDrawJson(parsed as Record<string, unknown>);
        if (output) return output;
      }
    } catch {
      // Fall through to tolerant extraction below.
    }
  }

  // 2) Tolerant extraction — survives malformed JSON by pulling each set's
  // fields out in order with regex.
  const mains = Array.from(text.matchAll(/"main"\s*:\s*\[([^\]]*)\]/g)).map(
    (m) =>
      m[1]
        .split(',')
        .map((n) => Number(n.trim()))
        .filter((n) => Number.isFinite(n)),
  );
  if (mains.length === 0) return null;

  // "special" may be a bare number (Powerball/Mega Millions) or an array (the
  // generic generator, e.g. EuroMillions' two Lucky Stars).
  const specials = Array.from(
    text.matchAll(/"special"\s*:\s*(\[[^\]]*\]|-?\d+(?:\.\d+)?)/g),
  ).map((m) =>
    toSpecials(
      m[1].startsWith('[')
        ? m[1]
            .slice(1, -1)
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean)
        : m[1],
    ),
  );
  const analyses = Array.from(
    text.matchAll(/"analysis"\s*:\s*"((?:[^"\\]|\\.)*)"/g),
  ).map((m) => m[1].replace(/\\"/g, '"').replace(/\\n/g, ' ').trim());

  const sets = mains
    .map((main, i) => ({
      main,
      specials: specials[i],
      analysis: analyses[i] || undefined,
    }))
    .filter((s) => s.main.length > 0);

  return sets.length > 0 ? { sets, notices: [] } : null;
}

/** Just the sets, for callers that don't need the game details. */
export function parseLotterySets(text?: string | null): LotterySet[] | null {
  return parseLotteryOutput(text)?.sets ?? null;
}

type Translate = (key: string, query?: Record<string, unknown>) => string;

const untranslated: Translate = (key, query) =>
  key.replace(/{{(\w+)}}/g, (_, name) => String(query?.[name] ?? ''));

/** Render sets as readable text for copy/export (never raw JSON). */
export function formatLotterySetsAsText(
  output: LotteryOutput,
  {
    specialLabel,
    t = untranslated,
  }: { specialLabel?: string; t?: Translate } = {},
): string {
  const bonus = output.bonusLabel || specialLabel || 'Bonus';
  return output.sets
    .map((set, i) => {
      let line = `${t('Set {{number}}', { number: i + 1 })}: ${
        // A 4D ticket reads as one number, 0287.
        output.digits ? set.main.join('') : set.main.join(', ')
      }`;
      if (set.specials?.length) {
        line += ` — ${bonus}: ${set.specials.join(', ')}`;
      }
      if (set.stats) {
        const { odd, even, low, high, sum } = set.stats;
        line += `\n${t('Odd/even')} ${odd}/${even} · ${t('Low/high')} ${low}/${high} · ${t('Sum')} ${sum}`;
      }
      if (set.analysis) {
        line += `\nAnalysis: ${set.analysis}`;
      }
      return line;
    })
    .join('\n\n');
}
